import React, {useEffect, useRef, useState} from "react";
import "./ViewNoteModal.css";
import toast from "react-hot-toast";
import html2canvas from "html2canvas";
import jsPDF from 'jspdf';

const ViewNoteModal = ({
  noteId,
  jobId,
  onClose,
  currentTheme,
  onViewAttachments,
  userid,
  scrollToNoteId,
}) => {
  const [currentNote, setCurrentNote] = useState(null);
  const [relatedNotes, setRelatedNotes] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentLightboxImages, setCurrentLightboxImages] = useState([]);
  const [currentLightboxIndex, setCurrentLightboxIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [jobDetails, setJobDetails] = useState(null);
  const [showJobInfo, setShowJobInfo] = useState(false);
  const [managerInfo, setManagerInfo] = useState(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [maxNotes, setMaxNotes] = useState('All');

  const noteRefs = useRef({});
  const chatContainerRef = useRef(null);
  const lightboxRef = useRef(null);

  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api`;

  // ---------- Emoji handling (unchanged) ----------
  const emojiPatterns = [
    /[\u{1F600}-\u{1F64F}]/gu,
    /[\u{1F300}-\u{1F5FF}]/gu,
    /[\u{1F680}-\u{1F6FF}]/gu,
    /[\u{1F700}-\u{1F77F}]/gu,
    /[\u{1F780}-\u{1F7FF}]/gu,
    /[\u{1F800}-\u{1F8FF}]/gu,
    /[\u{1F900}-\u{1F9FF}]/gu,
    /[\u{1FA00}-\u{1FA6F}]/gu,
    /[\u{1FA70}-\u{1FAFF}]/gu,
    /[\u{2600}-\u{26FF}]/gu,
    /[\u{2700}-\u{27BF}]/gu,
    /[\u{E000}-\u{F8FF}]/gu,
    /[\u{FE00}-\u{FE0F}]/gu,
    /[\u{1F1E6}-\u{1F1FF}]/gu,
  ];

  const isEmoji = (character) => {
    if (character.length > 1) {
      for (const pattern of emojiPatterns) {
        if (pattern.test(character)) return true;
      }
    }
    const garbledToEmojiMap = { "Ø>ÞàØ<ßI": "😀" };
    return garbledToEmojiMap[character] !== undefined;
  };

  const fixGarbledEmojis = (text) => {
    if (!text) return text;
    let fixedText = text;
    const garbledToEmojiMap = {
      "Ø>ÞàØ<ßI": "😀",
      "Ø>ÞàØ<ßJ": "😃",
      "Ø>ÞàØ<ßK": "😄",
      "Ø>ÞàØ<ßL": "😁",
      "Ø>ÞàØ<ßM": "😆",
      "Ø>ÞàØ<ßN": "😅",
      "Ø>ÞàØ<ßO": "😂",
      "Ø>ÞàØ<ßP": "🤣",
      "Ø>ÞàØ<ßQ": "😊",
      "Ø>ÞàØ<ßR": "😇",
      "Ø>ÞàØ<ßS": "🙂",
      "Ø>ÞàØ<ßT": "🙃",
      "Ø>ÞàØ<ßU": "😉",
      "Ø>ÞàØ<ßV": "😌",
      "Ø>ÞàØ<ßW": "😍",
      "Ø>ÞàØ<ßX": "😘",
      "Ø>ÞàØ<ßY": "😗",
      "Ø>ÞàØ<ßZ": "😙",
    };
    Object.keys(garbledToEmojiMap).forEach(garbled => {
      const regex = new RegExp(garbled.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      fixedText = fixedText.replace(regex, garbledToEmojiMap[garbled]);
    });
    return fixedText;
  };

  const createEmojiImage = (emoji, fontSize = 12) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const size = Math.max(fontSize * 1.2, 20);
      canvas.width = size;
      canvas.height = size;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = `bold ${fontSize}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", "Android Emoji", "EmojiOne Color", "Twemoji Mozilla", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'black';
      ctx.fillText(emoji, canvas.width / 2, canvas.height / 2);
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error creating emoji image:', error);
      return null;
    }
  };

  const splitTextIntoChunks = (text) => {
    if (!text) return [];
    const fixedText = fixGarbledEmojis(text);
    const chunks = [];
    let currentChunk = '';
    let inEmojiChunk = false;
    for (let i = 0; i < fixedText.length; i++) {
      const char = fixedText[i];
      const nextChar = fixedText[i + 1];
      const charCode = char.charCodeAt(0);
      const isLikelyEmoji = charCode > 0x00FF || (charCode >= 0x1F600 && charCode <= 0x1F64F);
      if (isLikelyEmoji) {
        if (currentChunk && !inEmojiChunk) {
          chunks.push({ type: 'text', content: currentChunk });
          currentChunk = '';
        }
        inEmojiChunk = true;
        currentChunk += char;
        if (nextChar && nextChar.charCodeAt(0) >= 0xFE00 && nextChar.charCodeAt(0) <= 0xFE0F) {
          currentChunk += nextChar;
          i++;
        }
      } else {
        if (currentChunk && inEmojiChunk) {
          chunks.push({ type: 'emoji', content: currentChunk });
          currentChunk = '';
        }
        inEmojiChunk = false;
        currentChunk += char;
      }
    }
    if (currentChunk) {
      chunks.push({ type: inEmojiChunk ? 'emoji' : 'text', content: currentChunk });
    }
    return chunks;
  };

  const renderTextWithEmojis = (pdf, text, x, y, fontSize = 10) => {
    if (!text) return x;
    const fixedText = fixGarbledEmojis(text);
    const chunks = splitTextIntoChunks(fixedText);
    let currentX = x;
    for (const chunk of chunks) {
      if (chunk.type === 'text') {
        let run = '';
        let currentFont = 'helvetica';
        for (let char of chunk.content) {
          const isEth = isEthiopic(char);
          const newFont = isEth ? 'NotoEthiopic' : 'helvetica';
          if (newFont !== currentFont) {
            if (run) {
              pdf.setFont(currentFont, 'normal');
              pdf.text(run, currentX, y);
              currentX += pdf.getTextWidth(run);
            }
            currentFont = newFont;
            run = char;
          } else {
            run += char;
          }
        }
        if (run) {
          pdf.setFont(currentFont, 'normal');
          pdf.text(run, currentX, y);
          currentX += pdf.getTextWidth(run);
        }
      } else if (chunk.type === 'emoji') {
        try {
          const emojiImage = createEmojiImage(chunk.content, fontSize);
          if (emojiImage) {
            const emojiSize = fontSize * 0.8;
            pdf.addImage(emojiImage, 'PNG', currentX, y - fontSize * 0.6, emojiSize, emojiSize);
            currentX += emojiSize * 0.9;
          } else {
            pdf.setFont('helvetica', 'normal');
            pdf.text(chunk.content, currentX, y);
            currentX += pdf.getTextWidth(chunk.content);
          }
        } catch (error) {
          pdf.setFont('helvetica', 'normal');
          pdf.text(chunk.content, currentX, y);
          currentX += pdf.getTextWidth(chunk.content);
        }
      }
    }
    return currentX;
  };

  // ---------- Helper: Normalize a note object (preserve replyIds and raw replies) ----------
  const normalizeNote = (n) => ({
    ...n,
    userName: n.UserName || n.userName,
    documentCount: n.DocumentCount || n.documentCount || 0,
    date: n.date || "",
    timeStamp: n.timeStamp || n.noteDate || "",
    inlineImageCount: n.inlineImageCount || 0,
    inlineImageUrls: n.inlineImageUrls || [],
    replyCount: n.replyCount || n.repliesCount || 0,
    replyIds: n.replyIds || [],
    // Keep raw replies (could be strings or incomplete objects) for fallback
    replayNotes: (n.replayNotes || n.replies || n.replyNotes || []).map(reply => {
      if (typeof reply === 'object' && reply !== null && reply.note !== undefined) return reply;
      if (typeof reply === 'string') return { note: reply, userName: 'Unknown', timeStamp: '', isReply: true };
      return reply;
    }),
    isReply: false
  });

  // ---------- Fetch full reply objects by IDs, with fallback to raw text ----------
  const fetchFullReplies = async (replyIds, originalRawReplies = []) => {
    if (!replyIds || replyIds.length === 0) return [];
    try {
      const replies = await Promise.all(
        replyIds.map(async (id, index) => {
          try {
            const res = await fetch(`${apiUrl}/SiteNote/GetSiteNoteById/${id}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const replyNote = data.siteNote || data;
            return {
              id: replyNote.id || id,
              note: replyNote.note || (originalRawReplies[index] || ''),
              userName: replyNote.userName || replyNote.UserName || 'Unknown',
              timeStamp: replyNote.timeStamp || replyNote.noteDate || replyNote.date || '',
              date: replyNote.date || '',
              inlineImageUrls: replyNote.inlineImageUrls || [],
              documentCount: replyNote.documentCount || 0,
              isReply: true
            };
          } catch (err) {
            console.error(`Failed to fetch reply ${id}:`, err);
            const fallbackText = originalRawReplies[index] || '[Failed to load reply]';
            return {
              id: id,
              note: fallbackText,
              userName: 'Unknown',
              timeStamp: '',
              date: '',
              inlineImageUrls: [],
              documentCount: 0,
              isReply: true
            };
          }
        })
      );
      return replies.filter(r => r).sort((a, b) => new Date(a.timeStamp) - new Date(b.timeStamp));
    } catch (error) {
      console.error('Error fetching replies batch:', error);
      return [];
    }
  };

  // ---------- Enrich a note with full replies ----------
  const enrichNoteWithReplies = async (note) => {
    // Already enriched with valid data?
    const hasValidReplies = note.replayNotes && note.replayNotes.length > 0 && 
                            note.replayNotes[0]?.userName && 
                            note.replayNotes[0]?.userName !== 'Unknown' &&
                            note.replayNotes[0]?.timeStamp;
    if (hasValidReplies) return note;
    
    if (!note.replyIds || note.replyIds.length === 0 || note.replyCount === 0) {
      return note;
    }
    
    const originalRawReplies = (note.replayNotes || []).map(r => 
      typeof r === 'string' ? r : (r.note || '')
    );
    
    const fullReplies = await fetchFullReplies(note.replyIds, originalRawReplies);
    if (fullReplies.length > 0) {
      return { ...note, replayNotes: fullReplies };
    }
    return note;
  };

  // ---------- Main fetch: note, related notes, and enrich with replies ----------
  const fetchNoteAndRelated = async () => {
    if (!noteId) return;
    setLoading(true);
    try {
      // 1. Fetch current note
      const noteRes = await fetch(`${apiUrl}/SiteNote/GetSiteNoteById/${noteId}`);
      if (!noteRes.ok) throw new Error("Failed to fetch note");
      const noteData = await noteRes.json();
      let current = normalizeNote(noteData);
      current = await enrichNoteWithReplies(current);
      setCurrentNote(current);

      // 2. Fetch job details if jobId provided
      if (jobId) {
        const jobRes = await fetch(`${apiUrl}/Job/GetJobById/${jobId}`);
        if (!jobRes.ok) throw new Error("Failed to fetch job details");
        const jobData = await jobRes.json();
        setJobDetails(jobData.job || jobData);
      }

      if (!jobId) {
        setRelatedNotes([current]);
        setLoading(false);
        return;
      }

      // 3. Fetch related notes
      const url = `${apiUrl}/SiteNote/GetSiteNotesByJobId?pageNumber=1&pageSize=25&jobId=${jobId}&userId=${userid}`;
      const relRes = await fetch(url);
      if (!relRes.ok) throw new Error("Failed to fetch related notes");
      const relData = await relRes.json();

      let related = (relData.siteNotes || [])
        .map(normalizeNote)
        .sort((a, b) => {
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const da = new Date(a.date);
          const db = new Date(b.date);
          const aF = da > today;
          const bF = db > today;
          if (aF && !bF) return 1;
          if (!aF && bF) return -1;
          if (da.getTime() !== db.getTime()) return da - db;
          return a.id - b.id;
        });

      // 4. Replace current note with enriched version
      related = related.map(n => n.id === current.id ? current : n);
      setRelatedNotes(related);

      // 5. Enrich other notes that have replyIds but missing full replies
      const notesToEnrich = related.filter(
        n => n.replyIds && n.replyIds.length > 0 &&
             (!n.replayNotes || n.replayNotes.length === 0 || !n.replayNotes[0]?.userName || n.replayNotes[0]?.userName === 'Unknown') &&
             n.id !== current.id
      );

      if (notesToEnrich.length > 0) {
        const enrichedNotes = await Promise.all(notesToEnrich.map(enrichNoteWithReplies));
        setRelatedNotes(prev =>
          prev.map(n => {
            const enriched = enrichedNotes.find(en => en.id === n.id);
            return enriched || n;
          })
        );
      }
    } catch (err) {
      console.error("ViewNoteModal error:", err);
      toast.error("Failed to load note");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  // ---------- Lightbox using URL array ----------
  const openLightboxWithImages = (imageUrls, startIndex = 0) => {
    if (!imageUrls || imageUrls.length === 0) return;
    setCurrentLightboxImages(imageUrls);
    setCurrentLightboxIndex(startIndex);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentLightboxImages([]);
    setCurrentLightboxIndex(0);
    document.body.style.overflow = 'auto';
  };

  const navigateLightbox = (direction) => {
    if (!currentLightboxImages.length) return;
    let newIndex = currentLightboxIndex;
    if (direction === 'next') {
      newIndex = (currentLightboxIndex + 1) % currentLightboxImages.length;
    } else {
      newIndex = (currentLightboxIndex - 1 + currentLightboxImages.length) % currentLightboxImages.length;
    }
    setCurrentLightboxIndex(newIndex);
  };

  // ---------- Thumbnail for inline images ----------
  const renderImageThumbnail = (note) => {
    const imageCount = note.inlineImageCount || 0;
    const imageUrls = note.inlineImageUrls || [];
    if (imageCount === 0) return null;

    return (
      <button
        className="inline-image-thumbnail"
        onClick={(e) => {
          e.stopPropagation();
          openLightboxWithImages(imageUrls, 0);
        }}
        title={`${imageCount} inline image(s)`}
      >
        <i className="fas fa-image" />
        <span className="image-count">{imageCount}</span>
      </button>
    );
  };

  // ---------- Render replies (uses full reply objects from replayNotes) ----------
  const renderNoteReplies = (note) => {
    const replies = note.replayNotes || [];
    if (replies.length === 0) return null;

    // Sort replies oldest first (already sorted by fetchFullReplies, but double-check)
    const sortedReplies = [...replies].sort((a, b) => {
      const timeA = new Date(a.timeStamp || a.date).getTime();
      const timeB = new Date(b.timeStamp || b.date).getTime();
      return timeA - timeB;
    });

    return (
      <div className="note-replies-container">
        <div className="replies-header">
          <i className="fas fa-reply" /> {sortedReplies.length} {sortedReplies.length === 1 ? 'Reply' : 'Replies'}
        </div>
        <div className="replies-list">
          {sortedReplies.map((reply) => (
            <div key={reply.id} className="reply-message">
              <div className="reply-content">
                <div className="reply-header">
                  <span className="reply-sender-name">
                    <i className="fas fa-reply fa-flip-horizontal" style={{ fontSize: '0.8em', marginRight: '5px', opacity: 0.7 }} />
                    {reply.userName}
                  </span>
                  <span className="reply-time" title={new Date(reply.timeStamp).toLocaleString()}>
                    {formatRelativeTime(reply.timeStamp)}
                  </span>
                </div>
                <div className="reply-text" dangerouslySetInnerHTML={{ __html: reply.note }} />
                {renderImageThumbnail(reply)}
                {reply.documentCount > 0 && (
                  <div className="reply-attachments">
                    <button
                      className="reply-attachment-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewAttachments(reply);
                      }}
                      title={`View ${reply.documentCount} attached file(s)`}
                    >
                      <span className="document-count-badge">({reply.documentCount}) </span>
                      <i className="fas fa-paperclip" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ---------- Helper functions ----------
  const formatDate = (iso) => {
    if (!iso) return "Invalid Date";
    const d = new Date(iso);
    return isNaN(d) ? "Invalid Date" : d.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "Invalid date";
    const now = new Date();
    const messageTime = new Date(timestamp);
    if (isNaN(messageTime)) return "Invalid date";
    const diffInSeconds = Math.floor((now - messageTime) / 1000);
    if (diffInSeconds < 60) return "just now";
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 7) return `${diffInWeeks} week${diffInWeeks > 1 ? "s" : ""} ago`;
    return messageTime.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const isEthiopic = (char) => {
    const code = char.charCodeAt(0);
    return (code >= 0x1200 && code <= 0x139F) ||
      (code >= 0x2D80 && code <= 0x2DDF) ||
      (code >= 0xAB00 && code <= 0xAB2F);
  };

  // ---------- PDF Generation ----------
  const generatePDF = async () => {
    let max = maxNotes;
    if (typeof max === 'string') {
      const t = max.trim().toLowerCase();
      if (t === '' || t === 'all') max = '10000';
    }
    if (isNaN(parseInt(max)) || parseInt(max) <= 0) {
      toast.error("Invalid number of notes.");
      return;
    }

    setPdfGenerating(true);
    try {
      const notesToPrint = relatedNotes.slice(-parseInt(max));
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
      let currentPage = 1;
      let yPosition = 20;
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 20;
      const lineHeight = 5;
      const noteSpacing = 2;
      const maxContentWidth = pageWidth - (2 * margin);
      const footerHeight = 5;

      // Header on first page
      if (currentPage === 1) {
        pdf.setFontSize(20);
        pdf.setFont("helvetica", "bold");
        const jobTitle = `Job Notes - ${currentNote?.siteNote?.job || jobDetails?.name || `Job_${jobId || noteId}`}`;
        const titleWidth = pdf.getTextWidth(jobTitle);
        const titleX = (pageWidth - titleWidth) / 2;
        pdf.text(jobTitle, titleX, yPosition);
        yPosition += 12;
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 10;
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "normal");
        if (currentNote?.siteNote?.workspace) {
          pdf.text(`Workspace: ${currentNote.siteNote.workspace}`, margin, yPosition);
          yPosition += lineHeight;
        }
        if (currentNote?.siteNote?.project) {
          pdf.text(`Project: ${currentNote.siteNote.project}`, margin, yPosition);
          yPosition += lineHeight;
        }
        if (jobDetails?.createdDate) {
          pdf.text(`Created: ${formatDate(jobDetails.createdDate)}`, margin, yPosition);
          yPosition += lineHeight;
        }
        yPosition += 5;
      }

      for (const note of notesToPrint) {
        // Build temporary DOM for the note
        const noteContainer = document.createElement('div');
        noteContainer.style.cssText = `
          width: ${maxContentWidth}mm;
          margin: 0 auto;
          padding: 8px;
          background: #f9f9f9;
          border-radius: 4px;
          margin-bottom: ${noteSpacing}mm;
          box-sizing: border-box;
        `;

        // Header: note text (truncated) + user/date
        const noteHeader = document.createElement('div');
        noteHeader.style.cssText = `display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px;`;
        const noteTextContainer = document.createElement('div');
        noteTextContainer.style.cssText = `flex: 1; min-width: 200px; font-weight: bold; color: #000; font-size: 13px; padding-right: 8px; line-height: 1.2;`;
        if (note.note) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = note.note;
          const plainText = tempDiv.textContent || tempDiv.innerText || '';
          noteTextContainer.textContent = plainText.length > 200 ? plainText.substring(0, 50) + '...' : plainText;
        } else {
          noteTextContainer.textContent = '[No text]';
        }
        noteHeader.appendChild(noteTextContainer);

        const userDateDiv = document.createElement('div');
        userDateDiv.style.cssText = `text-align: right; color: #666; font-size: 11px; white-space: nowrap; flex-shrink: 0; line-height: 1.2;`;
        const noteDate = new Date(note.timeStamp || note.date);
        const formattedDate = !isNaN(noteDate.getTime())
          ? noteDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
          : 'Invalid Date';
        userDateDiv.textContent = `${note.userName} - ${formattedDate}`;
        noteHeader.appendChild(userDateDiv);
        noteContainer.appendChild(noteHeader);

        // Full note text if truncated
        if (note.note) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = note.note;
          const plainText = tempDiv.textContent || tempDiv.innerText || '';
          if (plainText.length > 200) {
            const fullNoteDiv = document.createElement('div');
            fullNoteDiv.style.cssText = `margin-top: 6px; color: #333; font-size: 12px; line-height: 1.3;`;
            fullNoteDiv.innerHTML = note.note;
            noteContainer.appendChild(fullNoteDiv);
          }
        }

        // Inline images
        const imageUrls = note.inlineImageUrls || [];
        if (imageUrls.length > 0) {
          const imagesContainer = document.createElement('div');
          imagesContainer.style.cssText = `display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;`;
          imageUrls.forEach(url => {
            const imgContainer = document.createElement('div');
            imgContainer.style.cssText = `width: 200px; height: 120px; overflow: hidden; position: relative;`;
            const imgElement = document.createElement('img');
            imgElement.src = url;
            imgElement.alt = "Inline image";
            imgElement.style.cssText = `width: 100%; height: 100%; object-fit: cover;`;
            imgContainer.appendChild(imgElement);
            imagesContainer.appendChild(imgContainer);
          });
          noteContainer.appendChild(imagesContainer);
        }

        // Document count
        if (note.documentCount > 0) {
          const docCountDiv = document.createElement('div');
          docCountDiv.style.cssText = `margin-top: 6px; color: #666; font-size: 10px; line-height: 1.2;`;
          docCountDiv.textContent = `📎 Attachments: ${note.documentCount}`;
          noteContainer.appendChild(docCountDiv);
        }

        // Replies
        const replies = note.replayNotes || [];
        if (replies.length > 0) {
          const repliesContainer = document.createElement('div');
          repliesContainer.style.cssText = `margin-top: 10px; margin-left: 15px; border-left: 1px solid #ddd; padding-left: 8px;`;
          replies.forEach(reply => {
            const replyRow = document.createElement('div');
            replyRow.style.cssText = `margin-bottom: 4px; padding: 6px; background: #fff; border-radius: 2px; border: 1px solid #eee;`;
            const replyHeader = document.createElement('div');
            replyHeader.style.cssText = `display: flex; justify-content: space-between; align-items: flex-start;`;
            const replyIconText = document.createElement('div');
            replyIconText.style.cssText = `font-weight: 400; color: #000; font-size: 12px; display: flex; align-items: center; gap: 3px; flex: 1; padding-right: 8px; line-height: 1.2;`;
            if (reply.note) {
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = reply.note || '';
              const replyPlainText = tempDiv.textContent || tempDiv.innerText || '';
              replyIconText.innerHTML = '↩️ ' + replyPlainText;
            } else {
              replyIconText.innerHTML = '↩️ [No text]';
            }
            replyHeader.appendChild(replyIconText);
            const replyUserDate = document.createElement('div');
            replyUserDate.style.cssText = `text-align: right; color: #666; font-size: 10px; white-space: nowrap; flex-shrink: 0; line-height: 1.2;`;
            const replyDate = new Date(reply.timeStamp || reply.date);
            const replyFormattedDate = !isNaN(replyDate.getTime())
              ? replyDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
              : 'Invalid Date';
            replyUserDate.textContent = `${reply.userName} - ${replyFormattedDate}`;
            replyHeader.appendChild(replyUserDate);
            replyRow.appendChild(replyHeader);
            // Reply inline images
            if (reply.inlineImageUrls && reply.inlineImageUrls.length) {
              const replyImagesDiv = document.createElement('div');
              replyImagesDiv.style.cssText = `display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px;`;
              reply.inlineImageUrls.forEach(url => {
                const imgDiv = document.createElement('div');
                imgDiv.style.cssText = `width: 100px; height: 60px; overflow: hidden;`;
                const img = document.createElement('img');
                img.src = url;
                img.style.cssText = `width: 100%; height: 100%; object-fit: cover;`;
                imgDiv.appendChild(img);
                replyImagesDiv.appendChild(imgDiv);
              });
              replyRow.appendChild(replyImagesDiv);
            }
            repliesContainer.appendChild(replyRow);
          });
          noteContainer.appendChild(repliesContainer);
        }

        // Capture and add to PDF
        const tempContainer = document.createElement('div');
        tempContainer.style.cssText = `position: fixed; left: -9999px; top: -9999px; width: ${maxContentWidth}mm; background: white;`;
        tempContainer.appendChild(noteContainer);
        document.body.appendChild(tempContainer);
        await new Promise(resolve => setTimeout(resolve, 300));
        const canvas = await html2canvas(noteContainer, { scale: 2.0, useCORS: true, backgroundColor: '#ffffff' });
        document.body.removeChild(tempContainer);
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        const imgWidth = maxContentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        if (yPosition + imgHeight > pageHeight - margin - footerHeight) {
          pdf.addPage();
          currentPage++;
          yPosition = margin + 1;
        }
        pdf.addImage(imgData, 'JPEG', margin, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight + noteSpacing;
      }

      // Page numbers
      const totalPages = currentPage;
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        pdf.setPage(pageNum);
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(100, 100, 100);
        const pageNumberText = `Page ${pageNum} of ${totalPages}`;
        const textWidth = pdf.getTextWidth(pageNumberText);
        const xPosition = (pageWidth - textWidth) / 2;
        pdf.text(pageNumberText, xPosition, pageHeight - 10);
      }

      const jobName = currentNote?.siteNote?.job || jobDetails?.name || `Job_${jobId || noteId}`;
      const pdfFileName = jobName.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'notes';
      pdf.save(`${pdfFileName}_notes.pdf`);
      toast.success('PDF generated successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setPdfGenerating(false);
      setShowPrintDialog(false);
    }
  };

  // ---------- Effects ----------
  useEffect(() => {
    fetchNoteAndRelated();
  }, [noteId, jobId, userid]);

  useEffect(() => {
    const fetchManager = async (id) => {
      if (!id) {
        setManagerInfo(null);
        return;
      }
      try {
        const res = await fetch(`${apiUrl}/UserManagement/GetUserById/${id}`);
        if (!res.ok) throw new Error('Failed to fetch manager');
        const data = await res.json();
        setManagerInfo(data.user || data);
      } catch (err) {
        console.error('Failed to fetch manager info', err);
        setManagerInfo(null);
      }
    };
    if (jobDetails?.managerId) fetchManager(jobDetails.managerId);
  }, [jobDetails]);

  useEffect(() => {
    if (!loading && relatedNotes.length) {
      const target = scrollToNoteId || noteId;
      const el = noteRefs.current[target];
      if (el && chatContainerRef.current) {
        requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.style.transition = "background .4s ease";
          el.style.background = "#e3f2fd";
          setTimeout(() => {
            el.style.background = "";
          }, 2000);
        });
      }
    }
  }, [loading, relatedNotes, scrollToNoteId, noteId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          navigateLightbox('prev');
          break;
        case 'ArrowRight':
          navigateLightbox('next');
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentLightboxIndex]);

  // ---------- Loading state ----------
  if (loading) {
    return (
      <div className={`view-note-modal-overlay theme-${currentTheme}`}>
        <div className="view-note-modal loading">
          <div className="loading-spinner"></div>
          <p>Loading note...</p>
        </div>
      </div>
    );
  }

  if (!currentNote) return null;

  // ---------- Main JSX ----------
  return (
    <>
      <div className={`view-note-modal-overlay theme-${currentTheme}`} onClick={onClose}>
        <div className="view-note-modal" onClick={(e) => e.stopPropagation()}>
          <div className="whatsapp-header">
            <div className="header-left">
              <button className="back-button" onClick={onClose}>
                <i className="fas fa-arrow-left" />
              </button>
              <div className="contact-info">
                <div className="contact-name">Workspace: {currentNote.siteNote.workspace}</div>
                <div className="contact-project">Project: {currentNote.siteNote.project}</div>
                <div className="contact-status">Job: {currentNote.siteNote.job}</div>
              </div>
            </div>
            <div className="header-right">
              <button
                className="more-info-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowJobInfo((s) => !s);
                }}
                title="More job info"
              >
                <i className="fas fa-info-circle" />
              </button>
              {showJobInfo && (
                <div className="job-attributes">
                  {jobDetails?.type && (
                    <div className="attr">
                      <span className="attr-key">Type:</span>
                      <span className="attr-value">{jobDetails.type}</span>
                    </div>
                  )}
                  {jobDetails?.priorityName && jobDetails.priorityName !== "Unknown" && (
                    <div className="attr">
                      <span className="attr-key">Priority:</span>
                      <span className="attr-value">{jobDetails.priorityName}</span>
                    </div>
                  )}
                  {jobDetails?.startDate && (
                    <div className="attr">
                      <span className="attr-key">Start:</span>
                      <span className="attr-value">{formatDate(jobDetails.startDate)}</span>
                    </div>
                  )}
                  {jobDetails?.endDate && (
                    <div className="attr">
                      <span className="attr-key">End:</span>
                      <span className="attr-value">{formatDate(jobDetails.endDate)}</span>
                    </div>
                  )}
                  {jobDetails?.actualEndDate && (
                    <div className="attr">
                      <span className="attr-key">Actual End:</span>
                      <span className="attr-value">{formatDate(jobDetails.actualEndDate)}</span>
                    </div>
                  )}
                  {jobDetails?.managerId != null && (
                    <div className="attr manager-info">
                      <span className="attr-key">Manager:</span>
                      <div className="attr-value">
                        <div className="manager-name">
                          {managerInfo
                            ? `${managerInfo.fname || managerInfo.firstName || ''} ${managerInfo.lname || managerInfo.lastName || ''}`.trim()
                            : `ID: ${jobDetails.managerId}`}
                        </div>
                        {managerInfo?.email && <div className="manager-email">{managerInfo.email}</div>}
                      </div>
                    </div>
                  )}
                  {jobDetails?.createdDate && (
                    <div className="attr">
                      <span className="attr-key">Created:</span>
                      <span className="attr-value">{formatDate(jobDetails.createdDate)}</span>
                    </div>
                  )}
                </div>
              )}
              <button className="header-button" onClick={() => setShowPrintDialog(true)}>
                <i className="fas fa-print" />
              </button>
            </div>
          </div>

          <div className="whatsapp-chat" ref={chatContainerRef}>
            {relatedNotes.map((doc) => (
              <div
                key={doc.id}
                ref={(el) => (noteRefs.current[doc.id] = el)}
                className="message-row"
                id={`related-note-${doc.id}`}
              >
                <div
                  className={`message received ${doc.id === noteId ? "selected" : ""}`}
                  style={{
                    borderLeft: doc.id === noteId ? "4px solid #3498db" : "none",
                    border: "solid 1px #555",
                    background: doc.id === noteId ? "#f0f8ff" : "white",
                  }}
                >
                  <div className="message-content">
                    <div className="message-header">
                      <span className="sender-name">{doc.userName}</span>
                      <span className="message-time" title={new Date(doc.timeStamp).toLocaleString()}>
                        {formatRelativeTime(doc.timeStamp)}
                      </span>
                    </div>
                    <div className="message-date-below">{formatDate(doc.date)}</div>
                    <div className="message-text" dangerouslySetInnerHTML={{ __html: doc.note }} />
                    
                    {renderImageThumbnail(doc)}
                    {renderNoteReplies(doc)}
                  </div>
                </div>

                {doc.documentCount > 0 && (
                  <div className="paperclip-container">
                    <button
                      className="paperclip-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewAttachments(doc);
                      }}
                      title={`View ${doc.documentCount} attached file(s)`}
                    >
                      <span className="document-count-badge">({doc.documentCount}) </span>
                      <i className="fas fa-paperclip" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="whatsapp-footer">
            <button className="close-chat-button" onClick={onClose}>
              <i className="fas fa-times" /> Close
            </button>
          </div>
        </div>
      </div>

      {/* Print Dialog */}
      {showPrintDialog && (
        <div className="print-dialog-overlay" onClick={() => setShowPrintDialog(false)}>
          <div className="print-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Print Notes to PDF</h3>
            {pdfGenerating ? (
              <div className="pdf-generating">
                <i className="fas fa-spinner fa-spin" /> Generating PDF...
              </div>
            ) : (
              <>
                <div className="print-options">
                  <div className="print-option-group">
                    <label>
                      Maximum number of recent notes:
                      <input
                        type="text"
                        value={maxNotes}
                        onChange={(e) => setMaxNotes(e.target.value)}
                        placeholder="All"
                        className="notes-input"
                      />
                    </label>
                  </div>
                </div>
                <div className="buttons">
                  <button className="generate-btn" onClick={generatePDF}>
                    <i className="fas fa-file-pdf" /> Generate PDF
                  </button>
                  <button className="cancel-btn" onClick={() => setShowPrintDialog(false)}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && currentLightboxImages.length > 0 && (
        <div className="fullscreen-lightbox">
          <div className="lightbox-background" onClick={closeLightbox}></div>
          <div className="lightbox-image-wrapper" ref={lightboxRef}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <i className="fas fa-times" />
            </button>
            <div className="lightbox-counter">
              {currentLightboxIndex + 1} / {currentLightboxImages.length}
            </div>
            <img
              src={currentLightboxImages[currentLightboxIndex]}
              alt="Full size inline"
              className="lightbox-full-image"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/800x600?text=Image+Not+Found";
              }}
            />
            {currentLightboxImages.length > 1 && (
              <>
                <button className="lightbox-nav lightbox-prev" onClick={() => navigateLightbox('prev')}>
                  <i className="fas fa-chevron-left" />
                </button>
                <button className="lightbox-nav lightbox-next" onClick={() => navigateLightbox('next')}>
                  <i className="fas fa-chevron-right" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ViewNoteModal;